<template>
    <div @mousedown.passive="stopAutoSlide" @click.passive="stopAutoSlide" ref="root" class="gmf-slideshow-wrapper">
        <div class="gmf-slideshow-anchor" ref="anchor">
            <slot></slot>
        </div>
    </div>
</template>

<style>
.gmf-slideshow-wrapper {
    width: 100%;
    overflow: hidden;
    padding-bottom: var(--gmf-p-5);
}

.gmf-context .gmf-slideshow-header {
    padding: 0;
    width: 100%;
    margin-bottom: var(--gmf-p-3);
    margin-top: -5px;
    text-align: center;
    position: relative;
}

.gmf-context .gmf-slideshow-header .gmf-header {
    margin-bottom: 0;
    width: 100%;
}

.gmf-slideshow-anchor {
    display: flex;
    align-items: stretch;
    width: 100%;
    overflow-x: scroll;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    scroll-snap-stop: always;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
}

.gmf-slideshow-anchor::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
}

.gmf-slideshow-slide {
    align-self: center;
    max-height: 100%;
    width: 100%;
    flex-shrink: 0;
    flex-grow: 0;
    position: relative;
    scroll-snap-align: start;
    scroll-snap-stop: always;
}
</style>

<script>
export default {
    name: 'ReusableSwipe',

    props: {
        swipingEnabled: {
            type: Boolean,
            default: true,
        },

        length: {
            type: Number,
        },

        autoslideEnabled: {
            type: Boolean,
        },

        autoslideIntervalMs: {
            type: Number,
            default: 5000,
        },

        slide: {
            type: Number,
        }
    },

    data() {
        const internalSlide = this.slide;
        const numberOfComponents = this.length;

        return {
            numberOfComponents,
            internalSlide,
            autoSlideInterval: null,
            isProgrammaticScroll: false,
        };
    },

    mounted() {
        // Add scroll event listener
        this.$refs.anchor.addEventListener('scroll', this.handleScroll);
        
        if (this.autoslideEnabled) {
            this.startAutoSlide();
        }
    },

    beforeUnmount() {
        // Remove scroll event listener
        if (this.$refs.anchor) {
            this.$refs.anchor.removeEventListener('scroll', this.handleScroll);
        }
        
        this.stopAutoSlide();
    },
    
    methods: {
        handleScroll() {
            if (this.isProgrammaticScroll) return;
            
            const anchor = this.$refs.anchor;
            const currentSlide = Math.round(anchor.scrollLeft / anchor.offsetWidth);
            
            if (currentSlide !== this.internalSlide) {
                // Update internal slide without triggering another scroll
                this.internalSlide = currentSlide;
                this.$emit('update:slide', this.internalSlide);
                
                // Only stop auto-slide, don't call setSlide()
                if (this.autoslideEnabled) {
                    this.stopAutoSlide();
                }
            }
        },
        
        autoslide() {
            if (!this.autoslideEnabled) return;
            
            let nextSlide = this.internalSlide + 1;
            if (nextSlide >= this.numberOfComponents) {
                nextSlide = 0;
            }
            this.setSlide(nextSlide, false);
        },

        startAutoSlide() {
            if (!this.autoslideEnabled) return;

            if (this.autoSlideInterval) {
                clearInterval(this.autoSlideInterval);
            }
            this.autoSlideInterval = setInterval(this.autoslide, this.autoslideIntervalMs);
        },
        
        stopAutoSlide() {
            if (!this.autoslideEnabled) return;

            if (this.autoSlideInterval) {
                clearInterval(this.autoSlideInterval);
                this.autoSlideInterval = null;
            }
        },
        
        setSlide(slide, stopAutoSlide = true) {
            if (stopAutoSlide) {
                this.stopAutoSlide();
            }
            
            if (slide < 0) {
                slide = 0;
            } else if (slide >= this.numberOfComponents) {
                slide = this.numberOfComponents - 1;
            }

            this.internalSlide = slide;
            this.$emit('update:slide', this.internalSlide);
            
            // Scroll to the correct slide
            this.isProgrammaticScroll = true;
            const anchor = this.$refs.anchor;
            anchor.scrollTo({
                left: anchor.offsetWidth * slide,
                behavior: 'smooth'
            });
            
            setTimeout(() => {
                this.isProgrammaticScroll = false;
            }, 500);
        },
        
        next() {
            this.internalSlide++;
            this.$emit('update:slide', this.internalSlide);
            if (this.internalSlide >= this.numberOfComponents) {
                this.internalSlide = 0;
                this.$emit('update:slide', this.internalSlide);
            }
            
            this.setSlide(this.internalSlide);
        },
    },

    watch: {
        slide(v) {
            if (v !== this.internalSlide) {
                this.setSlide(v);
            }
        }
    }
};
</script>
